import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { API_BASE, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Loader2, Sparkles, Copy, Square, Brain, Settings as SettingsIcon, Info, Send, FolderKanban, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import VoiceButton from "@/components/VoiceButton";

const STARTER_CHIPS = [
  "Help me plan a product launch",
  "Brainstorm 10 content ideas for my brand",
  "Draft a landing page strategy",
  "Write a professional announcement post",
];
const QUICK_CHIPS = ["Continue", "Make it shorter", "Give me examples", "Turn this into action steps"];

/**
 * AlphaCore AI — the core intelligence.
 * Not a generic chat — a memory-aware, project-aware digital teammate.
 */
export default function AlphaCoreAI() {
  const [prompt, setPrompt] = useState("");
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [memoriesUsed, setMemoriesUsed] = useState([]);
  const [memoryEvent, setMemoryEvent] = useState(null);
  const [memoryCount, setMemoryCount] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [projectSheetOpen, setProjectSheetOpen] = useState(false);
  const [memorySheetOpen, setMemorySheetOpen] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/memory/settings`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("alphacore_token") || ""}` },
    }).then((r) => r.json()).then((s) => {
      if (s && s.memory_enabled) {
        // count is fetched from the smart dashboard normally, but for the badge we
        // do a quick facts hit — reuse the endpoint
        fetch(`${API_BASE}/memory/facts?limit=1`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("alphacore_token") || ""}` },
        }).then((r) => r.json()).then((d) => setMemoryCount(d?.total ?? 0));
      } else {
        setMemoryCount(0);
      }
    }).catch(() => {});
  }, []);

  const send = async (textOverride) => {
    const text = (textOverride ?? prompt).trim();
    if (!text) return;
    setOutput("");
    setMemoriesUsed([]);
    setMemoryEvent(null);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const token = localStorage.getItem("alphacore_token");
      const res = await fetch(`${API_BASE}/agents/text/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ prompt: text, agent: "alphacore-ai", sub_type: "general", tone: "professional", project_id: projectId }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const ln of lines) {
          const line = ln.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.token) setOutput((p) => p + ev.token);
            if (ev.memories_used) setMemoriesUsed(ev.memories_used);
            if (ev.memory_event) setMemoryEvent(ev.memory_event);
            if (ev.memory_command) {
              // manual command already completed; nothing more to do
            }
            if (ev.error) toast.error(ev.error);
          } catch {}
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message);
    } finally {
      setStreaming(false);
      setPrompt("");
    }
  };

  const stop = () => abortRef.current?.abort();

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 lg:pt-8 pb-4 max-w-[1400px] mx-auto kr-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="kr-overline" style={{ color: "rgb(var(--ac-purple))" }}>[ ALPHACORE AI ]</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2" data-testid="alphacore-ai-title">Your AI teammate</h1>
          <p className="text-white/55 text-sm mt-1.5">Memory-aware. Project-aware. Learns as you work.</p>
        </div>
        <div className="flex items-center gap-2">
          {memoryCount !== null && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 hover:bg-white/5 text-xs font-mono"
                  data-testid="memory-badge"
                >
                  <Brain className="h-3.5 w-3.5" style={{ color: memoryCount > 0 ? "rgb(var(--ac-purple))" : "rgba(255,255,255,0.3)" }} />
                  {memoryCount === 0 ? "Memory off" : `${memoryCount} memories`}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="bg-[#0A0A0E] border-white/10 text-white w-80">
                <div className="kr-overline">MEMORY</div>
                <div className="text-sm mt-2">
                  {memoryCount === 0
                    ? "AlphaCore AI hasn't learned anything yet. Say something worth remembering, or use \"Remember this: …\"."
                    : `AlphaCore AI is personalizing replies with ${memoryCount} stored memories.`}
                </div>
                <div className="mt-3 flex gap-2">
                  <Link to="/settings/memory">
                    <Button size="sm" variant="outline" className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="memory-open-settings">
                      <SettingsIcon className="h-3.5 w-3.5 mr-1.5" /> Manage
                    </Button>
                  </Link>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Conversation surface */}
      <div className="mt-6 kr-card p-4 sm:p-6 min-h-[52vh]">
        {memoryEvent && (
          <div className="mb-3 text-xs font-mono px-3 py-2 border border-white/10 rounded-sm inline-flex items-center gap-2" style={{ color: "rgb(var(--ac-purple))" }} data-testid="memory-event-banner">
            <Brain className="h-3.5 w-3.5" /> Memory action: {memoryEvent.replace(/_/g, " ")}
          </div>
        )}

        {memoriesUsed.length > 0 && (
          <div className="mb-4 flex items-start gap-2 text-xs" data-testid="memories-used-indicator">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.03]" style={{ color: "rgb(var(--ac-purple))" }}>
              <Brain className="h-3 w-3" /> Personalized with {memoriesUsed.length} {memoriesUsed.length === 1 ? "memory" : "memories"}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <button className="text-white/40 hover:text-white/70 flex items-center gap-1" data-testid="memories-used-inspect">
                  <Info className="h-3 w-3" /> Which?
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="bg-[#0A0A0E] border-white/10 text-white w-96">
                <div className="kr-overline mb-2">MEMORIES USED IN THIS REPLY</div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {memoriesUsed.map((m) => (
                    <div key={m.fact_id} className="text-xs">
                      <div className="font-mono uppercase tracking-wider text-white/40">{m.category} · {m.key}</div>
                      <div className="text-white/80">{m.value}</div>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {!output && !streaming && (
          <div className="py-10 text-center text-white/40 text-sm" data-testid="alphacore-ai-empty">
            <Sparkles className="h-6 w-6 mx-auto text-white/20" />
            <div className="mt-3">Ask anything. Say "Remember this: …" to save a fact. Say "What do you remember about me?" to inspect.</div>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {STARTER_CHIPS.map((c) => (
                <button key={c} onClick={() => send(c)} className="px-3.5 py-2 rounded-full border border-white/10 text-xs text-white/70 hover:text-white hover:border-[#FF3AA8]/50 hover:bg-white/[0.03] transition-colors min-h-[36px]" data-testid="starter-chip">
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}
        {streaming && !output && (
          <div className="py-14 flex flex-col items-center text-white/40">
            <Loader2 className="h-6 w-6 animate-spin" />
            <div className="text-xs mt-3 font-mono kr-pulse-soft">ALPHACORE AI THINKING…</div>
          </div>
        )}
        {output && (
          <div className="flex items-start justify-between gap-4">
            <motion.pre initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="whitespace-pre-wrap text-sm leading-relaxed text-white/90 font-body flex-1" data-testid="alphacore-ai-output">
              {output}
              {streaming && <span className="inline-block w-2 h-4 bg-white/80 ml-0.5 align-middle animate-pulse" />}
            </motion.pre>
            <Button onClick={() => { navigator.clipboard.writeText(output); toast.success("Copied"); }} variant="ghost" size="sm" className="hover:bg-white/5 hover:text-white shrink-0" data-testid="alphacore-ai-copy">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        {output && !streaming && (
          <div className="mt-4 ac-hscroll gap-2 pb-1" data-testid="quick-chips-row">
            {QUICK_CHIPS.map((c) => (
              <button key={c} onClick={() => send(c)} className="shrink-0 px-3.5 py-2 rounded-full border border-white/10 text-xs text-white/70 hover:text-white hover:border-[#FF3AA8]/50 hover:bg-white/[0.03] transition-colors min-h-[36px]" data-testid="quick-chip">
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Composer — sticky bottom, safe-area aware, sits above mobile bottom-nav */}
      <div
        className="mt-4 kr-card p-2.5 sm:p-3 sticky bottom-[calc(env(safe-area-inset-bottom)+80px)] lg:bottom-6 flex items-end gap-2 z-20"
        data-testid="alphacore-ai-composer"
      >
        <VoiceButton
          disabled={streaming}
          onTranscript={(text) => setPrompt((p) => (p ? p.trim() + " " : "") + text)}
        />
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          placeholder="Ask AlphaCore AI anything…"
          enterKeyHint="send"
          className="bg-[#050505] border-white/10 resize-none font-body min-h-[52px] text-base"
          data-testid="alphacore-ai-input"
        />
        {streaming ? (
          <Button onClick={stop} variant="outline" className="border-white/10 hover:bg-white/5 hover:text-white h-12 min-w-[48px] px-3 sm:px-4" data-testid="alphacore-ai-stop">
            <Square className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Stop</span>
          </Button>
        ) : (
          <Button onClick={() => send()} disabled={!prompt.trim()} className="text-white hover:opacity-90 h-12 min-w-[48px] rounded-md px-3 sm:px-5 font-semibold" style={{ background: "rgb(var(--ac-purple))" }} data-testid="alphacore-ai-send">
            <Send className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Send</span>
          </Button>
        )}
      </div>

      {/* Context row — memory + project sheets (mobile-friendly) */}
      <div className="mt-2 flex items-center gap-2">
        <button onClick={() => setMemorySheetOpen(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-xs text-white/60 hover:text-white hover:bg-white/[0.03] min-h-[36px]" data-testid="composer-memory-btn">
          <Brain className="h-3.5 w-3.5" style={{ color: "rgb(var(--ac-purple))" }} /> Memory
        </button>
        <button onClick={() => setProjectSheetOpen(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-xs text-white/60 hover:text-white hover:bg-white/[0.03] min-h-[36px] max-w-[60vw]" data-testid="composer-project-btn">
          <FolderKanban className="h-3.5 w-3.5" style={{ color: "rgb(var(--ac-blue))" }} />
          <span className="truncate">{projectId ? (projects.find((p) => p.project_id === projectId)?.name || "Project") : "Project"}</span>
        </button>
      </div>

      {/* Project selector sheet */}
      <Sheet open={projectSheetOpen} onOpenChange={setProjectSheetOpen}>
        <SheetContent side="bottom" className="bg-[#0B0D12] border-t border-white/10 text-white pb-[calc(env(safe-area-inset-bottom)+24px)] rounded-t-2xl max-h-[70vh] overflow-y-auto" data-testid="project-sheet">
          <SheetTitle className="kr-overline text-left" style={{ color: "rgb(var(--ac-blue))" }}>PROJECT CONTEXT</SheetTitle>
          <SheetDescription className="text-xs text-white/45 text-left">Attach this conversation to a project so memories and generations stay organized.</SheetDescription>
          <div className="mt-4 space-y-1.5">
            <button onClick={() => { setProjectId(null); setProjectSheetOpen(false); }} className="w-full flex items-center gap-3 p-3 rounded-lg border border-white/[0.08] hover:border-white/20 text-left min-h-[44px]" data-testid="project-sheet-none">
              <span className="text-sm flex-1">No project</span>
              {!projectId && <Check className="h-4 w-4" style={{ color: "rgb(var(--ac-green))" }} />}
            </button>
            {projects.map((p) => (
              <button key={p.project_id} onClick={() => { setProjectId(p.project_id); setProjectSheetOpen(false); }} className="w-full flex items-center gap-3 p-3 rounded-lg border border-white/[0.08] hover:border-white/20 text-left min-h-[44px]" data-testid={`project-sheet-${p.project_id}`}>
                <FolderKanban className="h-4 w-4 shrink-0" style={{ color: p.color || "#3AB4FF" }} />
                <span className="text-sm flex-1 truncate">{p.name}</span>
                {projectId === p.project_id && <Check className="h-4 w-4" style={{ color: "rgb(var(--ac-green))" }} />}
              </button>
            ))}
            {projects.length === 0 && <div className="text-xs text-white/35 py-4 text-center">No projects yet — create one from the dashboard.</div>}
          </div>
        </SheetContent>
      </Sheet>

      {/* Memory sheet */}
      <Sheet open={memorySheetOpen} onOpenChange={setMemorySheetOpen}>
        <SheetContent side="bottom" className="bg-[#0B0D12] border-t border-white/10 text-white pb-[calc(env(safe-area-inset-bottom)+24px)] rounded-t-2xl" data-testid="memory-sheet">
          <SheetTitle className="kr-overline text-left" style={{ color: "rgb(var(--ac-purple))" }}>MEMORY</SheetTitle>
          <SheetDescription className="text-xs text-white/45 text-left">
            {memoryCount === 0 || memoryCount === null
              ? "AlphaCore AI hasn't learned anything yet. Say \"Remember this: …\" to save a fact."
              : `AlphaCore AI is personalizing replies with ${memoryCount} stored memories.`}
          </SheetDescription>
          <div className="mt-4 flex gap-2 flex-wrap">
            <Link to="/settings/memory" onClick={() => setMemorySheetOpen(false)}>
              <Button variant="outline" className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="memory-sheet-manage">
                <SettingsIcon className="h-3.5 w-3.5 mr-1.5" /> Manage memory
              </Button>
            </Link>
            <Button variant="outline" onClick={() => { send("What do you remember about me?"); setMemorySheetOpen(false); }} className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="memory-sheet-inspect">
              <Brain className="h-3.5 w-3.5 mr-1.5" /> What do you remember?
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

